import React, { ReactElement, useEffect, useState } from 'react';
import {
  Block as BlockIcon,
  // Pets as PetsIcon,
  // Mail as MailIcon,
  Done as DoneIcon,
  Extension as ExtensionIcon,
  MonetizationOnOutlined as MonetizationOnOutlinedIcon,
  // Person as PersonIcon,
  RequestQuote as DailyValueIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';

import blockCleaning from '@/assets/icons/multicalendar/blockCleaning.svg';
import blockHost from '@/assets/icons/multicalendar/blockHost.svg';
import blockMaintenance from '@/assets/icons/multicalendar/blockMaintenance.svg';
import blockOwner from '@/assets/icons/multicalendar/blockOwner.svg';
import Finance from '@/assets/icons/multicalendar/finance.svg';
import Guest from '@/assets/icons/multicalendar/guest.svg';
import RingBell from '@/assets/icons/multicalendar/ring-bell.svg';
import airbnbLogo from '@/assets/icons/otas/airbnbLogo.svg';
import bookingLogo from '@/assets/icons/otas/bookingLogo.svg';
import decolarLogo from '@/assets/icons/otas/decolarLogo.svg';
import expediaLogo from '@/assets/icons/otas/expediaLogo.svg';
import homeawayLogo from '@/assets/icons/otas/homeawayLogo.svg';
import seazoneLogo from '@/assets/icons/otas/seazoneLogo.svg';
import staysLogo from '@/assets/icons/otas/staysLogo.svg';
import temporadalivreLogo from '@/assets/icons/otas/temporadalivreLogo.svg';
import testadorLogo from '@/assets/icons/otas/testadorLogo.svg';
import websiteLogo from '@/assets/icons/otas/websiteLogo.svg';

import { numberToCurrency } from '@/utils/Formatter';
import { translateBlockingReason } from '@/utils/Translator';

import { RequestOta } from '@/services/Property/types';
import { PaymentStatus, Reservation, ReservationStatus } from '@/services/Reservation/types';

import { useCheckBox } from '@/context/CheckBoxContext';
import { useReservation } from '@/context/ReservationContext';
import { useUser } from '@/context/UserContext';

import { useModal } from '@/hooks/ModalHook/useModal';
import { useReservationForm } from '@/hooks/ReservationHook/useReservationForm';

import FinanceForm from '../Calendar/Reservation/ModalFinance';
import GeneralForm from '../Calendar/Reservation/ModalGeneral';
import GuestForm from '../Calendar/Reservation/ModalGuest';
import LateralModal from '../LateralModal';
import Tooltip from '../Tooltip';

import {
  IconStatus,
  Indicators,
  Marker,
  MarkerContainer,
  MarkerContainerIcon,
  ReservationIcon,
} from './styles';

interface PropsMaker {
  id?: string;
  size: number;
  defaultSize?: number;
  ota: RequestOta;
  client?: string;
  price?: number;
  reservationStatus: ReservationStatus;
  paymentStatus?: PaymentStatus;
  adultGuests?: number;
  childrenGuests?: number;
  petIndicator?: boolean;
  emailIndicator?: boolean;
  blocked?: boolean;
  extension?: boolean;
  reason?: string | null;
  reservationData?: Reservation | any;
  bookings?: Reservation | any;
  propertyAddressId?: number | any;
  isShowingPrice?: boolean;
  isShowingStatus?: boolean;
  isShowingCanceledReservations?: boolean;
  widthMarker?: number;
  isWeeklyCalendarMarker?: boolean;
  showIndicators?: boolean;
  showIconStatus?: boolean;
  showIconReservation?: boolean;
  handleClick?: Function | any;
  isInitMarker?: boolean;
  isEndMarker?: boolean;
  totalDays?: number;
  isShowingMarker?: boolean;
  mobile?: boolean;
  dataCy?: string;
  isBookingOrBlockAutomatic?: boolean;
}

const ReservationMarker: React.FC<PropsMaker> = ({
  id,
  size,
  defaultSize = 2,
  ota,
  client,
  price,
  reservationStatus,
  paymentStatus,
  // adultGuests,
  // childrenGuests,
  // petIndicator,
  // emailIndicator,
  reservationData,
  bookings,
  propertyAddressId,
  blocked = false,
  widthMarker = 107,
  isWeeklyCalendarMarker = false,
  showIndicators = true,
  showIconStatus = false,
  showIconReservation = false,
  isInitMarker = false,
  isEndMarker = false,
  handleClick,
  totalDays = 0,
  isShowingMarker = true,
  mobile = false,
  dataCy = '',
  isBookingOrBlockAutomatic = false,
}) => {
  const { setReservationData } = useReservation();
  const [extension, setExtension] = useState<boolean>(false);
  const [isBlocked, setIsBlocked] = useState<boolean>(blocked);
  const [status, setStatus] = useState<ReservationStatus>(reservationStatus);
  const [isPaid, setIsPaid] = useState<PaymentStatus>('unpaid');
  const { handleResetSelection } = useReservationForm();
  const [openLateralModal, setOpenLateralModal] = useState(false);
  const [modalTitle, setModalTitle] = useState<string>('Dados da Reserva');
  const { handleOpen, modalActive, handleModalActive } = useModal();
  const [guestName, setGuestName] = useState<string>(!client ? '' : client.trim());
  const [fullGuestName, setFullGuestName] = useState<string>(!client ? '' : client.trim());
  const { userInformation } = useUser();
  const roles = userInformation?.roles || [];

  let options = [];
  if (roles.includes('Host')) {
    options = [
      { icon: RingBell, alt: 'Ring Bell', title: 'Dados da reserva' },
      { icon: Guest, alt: 'Guest', title: 'Hóspede' },
    ];
  } else {
    options = [
      { icon: RingBell, alt: 'Ring Bell', title: 'Dados da reserva' },
      { icon: Guest, alt: 'Guest', title: 'Hóspede' },
      { icon: Finance, alt: 'Finance', title: 'Financeiro' },
    ];
  }

  const { isShowingStatus, isShowingCanceledReservations } = useCheckBox();

  const reservationStatusComponent: Record<ReservationStatus, ReactElement> = {
    Concluded: <DoneIcon className="reservationIcon" />,
    'Pre-booking': <UpdateIcon className="reservationIcon" />,
    Canceled: <BlockIcon className="reservationIcon" />,
    Active: <DoneIcon className="reservationIcon" />,
    'In progress': <div />,
  };

  const ImageOta: Record<string, ReactElement> = {
    airbnb: <img src={airbnbLogo} alt={ota.name} />,
    booking: <img src={bookingLogo} alt={ota.name} />,
    expedia: <img src={expediaLogo} alt={ota.name} />,
    homeaway: <img src={homeawayLogo} alt={ota.name} />,
    stays: <img src={staysLogo} alt={ota.name} />,
    temporadalivre: <img src={temporadalivreLogo} alt={ota.name} />,
    contrato: <img src={seazoneLogo} alt={ota.name} />,
    website: <img src={websiteLogo} alt={ota.name} />,
    testador: <img src={testadorLogo} alt={ota.name} />,
    decolar: <img src={decolarLogo} alt={ota.name} />,
  };

  const ImageBlock: Record<string, ReactElement> = {
    Cleaning: <img src={blockCleaning} alt="imagem do bloqueio de limpeza" />,
    Host: <img src={blockHost} alt="imagem do bloqueio de anfitrião" />,
    Maintenance: <img src={blockMaintenance} alt="imagem do bloqueio de manutenção" />,
    'Owner use': <img src={blockOwner} alt="imagem do bloqueio de proprietário" />,
  };

  const [reasonTranslate, setReasonTranslate] = useState<string>('');
  useEffect(() => {
    const blockingReason = translateBlockingReason(reservationData?.blocking_reason);
    setReasonTranslate(blockingReason);
  }, [reservationData?.blocking_reason]);

  const Form: React.FC = () => {
    if (modalActive === 0) {
      return (
        <GeneralForm
          totalDays={totalDays}
          bookings={bookings}
          propertyAddressId={propertyAddressId}
        />
      );
    }
    if (modalActive === 1) {
      return <GuestForm />;
    }
    return <FinanceForm />;
  };

  useEffect(() => {
    handleModalActive(0);
  }, []);

  const handleSetModalTitle = () => {
    if (modalActive === 0) {
      if (reservationData.is_blocking) {
        setModalTitle('Dados do Bloqueio');
      } else {
        setModalTitle('Dados da Reserva');
      }
    } else if (modalActive === 1) {
      setModalTitle('Hóspede');
    } else {
      setModalTitle('Financeiro');
    }
  };

  useEffect(() => {
    handleSetModalTitle();
  }, [modalActive]);

  useEffect(() => {
    if (reservationData?.is_early_extension || reservationData?.is_late_extension) {
      setExtension(true);
    }
  }, [reservationData?.is_early_extension, reservationData?.is_late_extension]);

  useEffect(() => {
    if (reservationData?.is_blocking) {
      setIsBlocked(true);
    }
  }, [reservationData?.is_blocking]);

  useEffect(() => {
    setStatus(reservationStatus);
    if (reservationStatus === 'Canceled') {
      setIsPaid('canceled');
      setExtension(false);
      setIsBlocked(false);
    }
  }, [reservationStatus]);

  const getGuestName = () => {
    const name = !client ? '' : client.trim();
    const nameWithoutBlankSpaces = name.replace(/\s/g, '-');
    const nameSplitted = nameWithoutBlankSpaces.split('-');

    let nameFormatted = '';
    if (nameSplitted.length > 0) {
      nameSplitted.forEach((item: string) => {
        if (item !== '') {
          nameFormatted += ` ${item}`;
        }
      });
    } else {
      nameFormatted = name;
    }

    setFullGuestName(nameFormatted);

    if (size < 3 && nameFormatted.length >= 10) {
      nameFormatted = `${nameFormatted.substring(0, 5)}...`;
    }
    return mobile && size <= 3 && nameFormatted.length >= 10
      ? `${nameFormatted.substring(0, 8)}...`
      : nameFormatted;
  };

  useEffect(() => {
    setGuestName(getGuestName());
  }, [client, size]);

  const handleOpenlLateral = () => {
    handleOpen(false);
    setOpenLateralModal(true);
    setReservationData(reservationData);
  };

  const handleOpenModal = () => (!isWeeklyCalendarMarker ? handleOpenlLateral() : handleClick());

  const handleCloseAll = () => {
    handleResetSelection();
    setOpenLateralModal(false);
  };

  const statusPayment = (statusOfPayment: string): string => {
    if (statusOfPayment === 'fullyPaid') {
      return 'Totalmente pago';
    }
    if (statusOfPayment === 'unpaid') {
      return 'Não pago';
    }
    if (statusOfPayment === 'partiallyPaid') {
      return 'Parcialmente pago';
    }
    return 'Cancelado';
  };

  useEffect(() => {
    if (paymentStatus) {
      setIsPaid(paymentStatus);
    }
  }, [paymentStatus]);

  return (
    <>
      <Marker
        key={id}
        data-cy={dataCy}
        size={defaultSize}
        isInitMarker={isInitMarker}
        isEndMarker={(totalDays < 3 && !mobile) || isEndMarker}
        widthMarker={widthMarker}
        reservationStatus={status}
        blocked={isBlocked}
        reason={!!reservationData?.blocking_reason}
        extension={extension}
        onClick={() => handleOpenModal()}
        isShowingCanceledReservations={
          roles.includes('Host') ? false : isShowingCanceledReservations
        }
        isShowingMarker={isShowingMarker}
        mobile={mobile}
        isBookingOrBlockAutomatic={isBookingOrBlockAutomatic}
      >
        <MarkerContainerIcon
          reservationStatus={status}
          paymentStatus={isPaid}
          showIcon={isInitMarker}
          data-cy="marker"
          mobile={mobile}
        >
          {/* Aqui está renderizando o ícone */}
          {!reservationData?.blocking_reason
            ? ImageOta[ota.name.toLowerCase().trim()]
            : ImageBlock[reservationData?.blocking_reason]}
        </MarkerContainerIcon>
        <Tooltip text={blocked ? 'Bloqueio' : fullGuestName}>
          <MarkerContainer
            size={size}
            isWeeklyCalendarMarker={isWeeklyCalendarMarker}
            isInitMarker={isInitMarker}
            isEndMarker={isEndMarker}
            widthMarker={widthMarker}
            reservationStatus={status}
            blocked={isBlocked}
            reason={!!reservationData?.blocking_reason}
            mobile={mobile}
          >
            {!reservationData?.is_blocking ? (
              <>
                <div className="wrapper-name">
                  <div className="name">
                    {!mobile && isWeeklyCalendarMarker && <span>Reserva</span>}
                    <span>{guestName}</span>
                  </div>
                  <Indicators
                    className="indicators"
                    showIndicators={size < 3 ? false : showIndicators}
                    mobile={mobile}
                  >
                    <DailyValueIcon />
                    <div className="dailyValueIcon">
                      <span>{reservationData?.daily_net_value}</span>
                    </div>
                    {/* <div className="adultIcon">
                      <PersonIcon />
                      <span>{adultGuests}</span>
                    </div>
                    <div className="childIcon">
                      <PersonIcon />
                      <span>{childrenGuests}</span>
                    </div>
                    {petIndicator ? <PetsIcon className="petsIcon" /> : null}
                    {emailIndicator ? <MailIcon /> : null} */}
                  </Indicators>
                </div>
                <div className="totalPrice">
                  {price !== undefined && !roles.includes('Host') && (
                    <>
                      <span className="currency">R$ </span>
                      <span className="price">{numberToCurrency(price, false)}</span>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="name">{`Bloqueio ${size > 2 ? `- ${reasonTranslate}` : ''}`}</div>
            )}

            {size > 3 && !isBlocked && !isWeeklyCalendarMarker && (
              <ReservationIcon showIcon={showIconReservation}>
                {(!isBlocked && !extension && reservationStatusComponent[status]) ||
                  (extension && <ExtensionIcon className="reservationIcon" />)}
              </ReservationIcon>
            )}

            {!isWeeklyCalendarMarker && (
              <IconStatus
                className="status"
                paymentStatus={isPaid}
                reservationStatus={status}
                isShowingStatus={roles.includes('Host') ? false : isShowingStatus}
                showIcon={showIconStatus}
                isBookingOrBlockAutomatic={isBookingOrBlockAutomatic}
              >
                {reservationData?.blocking_reason ? null : (
                  <Tooltip text={statusPayment(isPaid)}>
                    <MonetizationOnOutlinedIcon className="paymentIcon" />
                  </Tooltip>
                )}
              </IconStatus>
            )}
          </MarkerContainer>
        </Tooltip>
      </Marker>

      <LateralModal
        headerTitle={modalTitle}
        open={openLateralModal}
        setOpen={setOpenLateralModal}
        onClose={handleCloseAll}
        activeTabs
        options={!reservationData?.is_blocking ? options : []}
      >
        <Form />
      </LateralModal>
    </>
  );
};

export default ReservationMarker;
